import React, { forwardRef, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    FormGroup,
    FormText,
    Input,
    InputGroup,
    InputGroupAddon,
    Label,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../store/selectors';
import { ProposeClosingDate } from '../../../slices/marketplace/closing-date/thunks/propose-closing-date.thunk';
import { AcceptClosingDateProposal } from '../../../slices/marketplace/closing-date/thunks/accept-closing-date-proposal.thunk';
import { DeclineClosingDateProposal } from '../../../slices/marketplace/closing-date/thunks/decline-closing-date-proposal.thunk';

export type ClosingDateActionMode = 'propose' | 'accept' | 'decline';

const FORM_TITLES: Record<ClosingDateActionMode, string> = {
    propose: 'Propose New Date',
    accept: 'Accept Proposed Date',
    decline: 'Decline Proposed Date',
};

const COMMENT_PLACEHOLDER =
    'A comment is required for every proposal, acceptance, or decline…';
const COMMENT_REQUIRED_MESSAGE =
    'A comment is required for every proposal, acceptance, or decline.';
const DATE_REQUIRED_MESSAGE = 'Please choose a proposed closing date.';

const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <div
        ref={ref}
        className="btn closing-date-action-form__date-input"
        onClick={onClick}>
        {value || 'Select a date'}
    </div>
));

export interface ClosingDateActionFormProps {
    engagementId: number;
    mode: ClosingDateActionMode;
    proposalId?: string | null;
    onCancel: () => void;
    onDone: () => void;
}

export const ClosingDateActionForm = ({
    engagementId,
    mode,
    proposalId,
    onCancel,
    onDone,
}: ClosingDateActionFormProps) => {
    const dispatch = useDispatch() as any;
    const isSubmitting = useStateSelector(
        (state) => state.closingDateState.isSubmitting
    );
    const datePickerRef = useRef<any>(null);
    const [proposedDate, setProposedDate] = useState<Date | null>(null);
    const [comment, setComment] = useState('');
    const [fieldError, setFieldError] = useState('');

    const dispatchAction = () => {
        if (mode === 'propose') {
            return dispatch(
                ProposeClosingDate({
                    engagementId,
                    proposedDate: proposedDate as Date,
                    comment: comment.trim(),
                })
            );
        }
        if (mode === 'accept') {
            return dispatch(
                AcceptClosingDateProposal({
                    engagementId,
                    proposalId: proposalId as string,
                    comment: comment.trim(),
                })
            );
        }
        return dispatch(
            DeclineClosingDateProposal({
                engagementId,
                proposalId: proposalId as string,
                comment: comment.trim(),
            })
        );
    };

    const handleSubmit = () => {
        if (!comment.trim()) {
            setFieldError(COMMENT_REQUIRED_MESSAGE);
            return;
        }
        if (mode === 'propose' && !proposedDate) {
            setFieldError(DATE_REQUIRED_MESSAGE);
            return;
        }
        setFieldError('');
        dispatchAction()
            .unwrap()
            .then(() => onDone())
            .catch(() => {});
    };

    return (
        <div className="closing-date-action-form">
            <h5 className="closing-date-action-form__title">
                {FORM_TITLES[mode]}
            </h5>
            {mode === 'propose' && (
                <FormGroup>
                    <Label>
                        Proposed Closing Date{' '}
                        <span className="text-danger">*</span>
                    </Label>
                    <InputGroup>
                        <InputGroupAddon
                            addonType="prepend"
                            onClick={() => datePickerRef.current?.setOpen(true)}>
                            <div className="input-group-text">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                            </div>
                        </InputGroupAddon>
                        <DatePicker
                            ref={datePickerRef}
                            selected={proposedDate}
                            onChange={(value: Date) => setProposedDate(value)}
                            minDate={new Date()}
                            dateFormat="MM/dd/yyyy"
                            className="form-control"
                            popperProps={{ strategy: 'fixed' }}
                            customInput={<DateControlInput />}
                        />
                    </InputGroup>
                </FormGroup>
            )}
            <FormGroup>
                <Label>
                    Comment <span className="text-danger">*</span>
                </Label>
                <Input
                    type="textarea"
                    rows={3}
                    value={comment}
                    onChange={(e: any) => setComment(e.target.value)}
                    placeholder={COMMENT_PLACEHOLDER}
                />
            </FormGroup>
            {fieldError && <FormText color="danger">{fieldError}</FormText>}
            <div className="closing-date-action-form__buttons">
                <ButtonLoader
                    buttonText="Submit"
                    loaderButtonText="Submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    className="btn btn-primary"
                />
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onCancel}>
                    Cancel
                </button>
            </div>
        </div>
    );
};
